import { useFocusRing } from '@react-aria/focus';
import { useTableRow } from '@react-aria/table';
import { mergeProps } from '@react-aria/utils';
import { TableState } from '@react-stately/table';
import { GridNode } from '@react-types/grid';
import { cx, VariantProps } from 'class-variance-authority';
import React, { useImperativeHandle, useRef } from 'react';
import { tableRowClassNames } from './styles';

interface Props<T> {
    item: GridNode<T>;
    state: TableState<T>;
    isInfinityScroll?: boolean;
    children?: React.ReactNode;
}

type NativeAttrs = Omit<React.HTMLAttributes<unknown>, keyof Props<any>>;

export type TableRowProps<T = unknown> = Props<T> & VariantProps<typeof tableRowClassNames> & NativeAttrs;

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
    ({ children, item, isInfinityScroll, hoverable, state, ...props }, ref: React.Ref<HTMLTableRowElement | null>) => {
        const tableRowRef = useRef<HTMLTableRowElement | null>(null);

        useImperativeHandle(ref, () => tableRowRef?.current);

        const { rowProps } = useTableRow({ node: item }, state, tableRowRef);

        const { isFocusVisible, focusProps } = useFocusRing();
        const isDisabled = state.disabledKeys.has(item.key);

        return (
            <tr
                ref={tableRowRef}
                {...mergeProps(rowProps, focusProps, props)}
                className={cx(
                    tableRowClassNames({ isDisabled, isInfinityScroll, isFocusVisible, hoverable, isSelected: !!rowProps['aria-selected'] }),
                    props.className
                )}
            >
                {children}
            </tr>
        );
    }
);

TableRow.displayName = 'TableRow';

export default TableRow;
